import { EmployeeValidator } from './employee-validator.base'
import { EmployeeService } from 'src/app/services/employee/employee.service'
import { employeeAPIModel } from 'src/app/models/api.model'
import { WebService } from 'src/app/services/api/web.service'
import { ServiceUrlConstants } from 'src/app/constants/service-url-constants'

import {
  EMP_DISPLAY_ID,
  EMPLOYEE_ID,
  FIRST_NAME,
  MIDDLE_NAME,
  LAST_NAME,
  MARITAL_STATUS,
  GENDER,
  DOB as EXCEL_DOB,
  DATE_OF_JOINING,
  FATHER_NAME,
  PRIMARY_EMAIL_ID,
  SECONDARY_EMAIL_ID,
  PRIMARY_MOBILE_NUMBER,
  SECONDARY_MOBILE_NUMBER,
  STATUS,
  NATIONALITY,
  ROLE_CODE,
  NOTIFICATION_PREFERENCES,
  AADHAR_NUMBER,
  AADHAAR_NAME,
  PAN_NUMBER,
  PAN_NAME,
  NOMINEE_NAME,
  NOMINEE_RELATIONSHIP,
  NOMINEE_DOB,
  UAN_NUMBER,
  PF_NUMBER,
  CURRENT_PF_ACCOUNT_NUMBER,
  BANK_ACCOUNT_NUMBER,
  IFSC_CODE,
  INTERNATIONAL_WORKER,
  PASSPORT_NAME,
  PASSPORT_NUMBER,
  PASSPORT_VALIDITY_FROM,
  PASSPORT_VALIDITY_TO,
  COUNTRY_OF_ISSUE,
  DESIGNATION,
  DEPARTMENT as EXCEL_DEPARTMENT,
  LOCATION as EXCEL_LOCATION,
  BRANCH,
  PHYSICALLY_HANDICAP,
  BASIC_WAGES,
  EPF_WAGES_ON_JOINING,
  DATE_OF_LEAVING,
  REASON_FOR_LEAVING,
  EPF_APPLICABILITY,
  EPS_APPLICABILITY,
  GROSS_WAGES,
  EMPLOYEE_TYPE,
  DOOR_NO,
  STREET,
  AREA,
  CITY,
  STATE,
  COUNTRY,
  PINCODE,
  HIGHER_PENSION_APPLICABILITY,
  DATE_OF_JOINING_IN_EPF,
  SPOUSE_NAME,
  PENSION_PAYMENT_ORDER_NUMBER,
  DATE_OF_EXIT_FROM_PREVIOUS_EMPLOYMENT,
  ESI_NUMBER,
  MONTH_WORKING_DAYS,
  WORKED_DAYS,
  BASIC_PAY,
  HRA,
  LTA,
  PERQ_ALLOWANCE,
  SPECIAL_ALLOWANCE,
  TELEPHONE_ALLOWANCE,
  FOOD_ALLOWANCE,
  GROSS_EARNINGS,
  LOP,
  TDS,
  EPS,
  ADVANCE_PAY,
  SALARY_DUE_NET_SALARY,
  NATURE_OF_WORK,
  DAILY_HOURS_OF_WORK,
  INTERVALS_OF_REST,
  OVERTIME_WAGES,
  SCHEME_CERTIFICATE_NUMBER,
  MAIL_TRIGGER,
  NEW_JOINER_ACTIVITY
} from 'src/app/constants/excel.columns'
import { IColumn } from './abstract-validator.interface'

export abstract class CommonInsertEmployeeValidator extends EmployeeValidator {
  public override _header: Map<string, IColumn>

  override bulkImportApiFn: Function
  override formatEmployees: Function = employees => employees

  override createAddress: Function = (row: any) => {
    if (row?.doorno && row?.street && row?.pin && row?.city && row?.state && row?.country) {
      return {
        address1: row?.doorno,
        address2: row?.street,
        address3: row?.area,
        city: row?.city,
        state: row?.state,
        country: row?.country,
        pin: row?.pin
      }
    }
    return null
  }

  constructor(
    employeeService: EmployeeService,
    u: employeeAPIModel,
    webService: WebService,
    sendNewJoineeEmail: boolean,
    header: Map<string, IColumn>
  ) {
    super(employeeService, u, webService)

    this._header = header

    this.bulkImportApiFn = (rowsToSave: any, webservice: WebService) => {
      const endPoint = `${ServiceUrlConstants.API_SAVE_BULK_EMPLOYEE}?sendNewJoineeEmail=${sendNewJoineeEmail}`
      return webservice.post(endPoint, rowsToSave, null, [], false)
    }

    this.templateName = 'Employee_Import_Template.xlsx'
  }
}

export class InsertEmployeeValidator extends CommonInsertEmployeeValidator {
  constructor(
    employeeService: EmployeeService,
    u: employeeAPIModel = null,
    webService: WebService,
    sendNewJoineeEmail: boolean
  ) {
    const header = new Map(getCommonEmployeeHeader())
    header.set('Mail Trigger', MAIL_TRIGGER)
    header.set('New Joiner Activity', NEW_JOINER_ACTIVITY)
    super(employeeService, u, webService, sendNewJoineeEmail, header)
  }
}

export class IntelInsertEmployeeValidator extends CommonInsertEmployeeValidator {
  constructor(
    employeeService: EmployeeService,
    u: employeeAPIModel = null,
    webService: WebService,
    sendNewJoineeEmail: boolean
  ) {
    const header = new Map(getCommonEmployeeHeader())
    super(employeeService, u, webService, sendNewJoineeEmail, header)
  }
}

export class OnboardingInsertEmployeeValidator extends CommonInsertEmployeeValidator {
  constructor(
    employeeService: EmployeeService,
    u: employeeAPIModel = null,
    webService: WebService,
    sendNewJoineeEmail: boolean
  ) {
    super(employeeService, u, webService, sendNewJoineeEmail, getCommonEmployeeHeader())
  }
}

export const getCommonEmployeeHeader = () =>
  new Map<string, IColumn>([
    ['Display ID', EMP_DISPLAY_ID],
    ['Employee ID', EMPLOYEE_ID],
    ['First Name', FIRST_NAME],
    ['Middle Name', MIDDLE_NAME],
    ['Last Name', LAST_NAME],
    ['Marital Status', MARITAL_STATUS],
    ['Gender', GENDER],
    ['DOB', EXCEL_DOB],
    ['Joining Date', DATE_OF_JOINING],
    ['Father Name', FATHER_NAME],
    ['Primary Email ID', PRIMARY_EMAIL_ID],
    ['Secondary Email ID', SECONDARY_EMAIL_ID],
    ['Primary Mobile Number', PRIMARY_MOBILE_NUMBER],
    ['Secondary Mobile Number', SECONDARY_MOBILE_NUMBER],
    ['Status', STATUS],
    ['Nationality', NATIONALITY],
    ['Role Code', ROLE_CODE],
    ['Notification Preferences', NOTIFICATION_PREFERENCES],
    ['Aadhaar Number', AADHAR_NUMBER],
    ['Aadhaar Name', AADHAAR_NAME],
    ['Pan Number', PAN_NUMBER],
    ['Pan Name', PAN_NAME],
    ['Nominee Name', NOMINEE_NAME],
    ['Nominee Relationship', NOMINEE_RELATIONSHIP],
    ['Nominee DOB', NOMINEE_DOB],
    ['UAN Number', UAN_NUMBER],
    ['PF Number', PF_NUMBER],
    ['Current PF Account Number', CURRENT_PF_ACCOUNT_NUMBER],
    ['Bank Account number', BANK_ACCOUNT_NUMBER],
    ['IFSC Code', IFSC_CODE],
    ['Is International Worker', INTERNATIONAL_WORKER],
    ['Passport Name', PASSPORT_NAME],
    ['Passport Number', PASSPORT_NUMBER],
    ['Passport Validity From', PASSPORT_VALIDITY_FROM],
    ['Passport Validity To', PASSPORT_VALIDITY_TO],
    ['Country Of Issue', COUNTRY_OF_ISSUE],
    ['Designation', DESIGNATION],
    ['Department', EXCEL_DEPARTMENT],
    ['Location', EXCEL_LOCATION],
    ['Branch', BRANCH],
    ['Is Physically Disabled', PHYSICALLY_HANDICAP],
    ['Basic Wages', BASIC_WAGES],
    ['EPF Wages On Joining', EPF_WAGES_ON_JOINING],
    ['Date Of Leaving', DATE_OF_LEAVING],
    ['Reason For Leaving', REASON_FOR_LEAVING],
    ['EPF Applicability', EPF_APPLICABILITY],
    ['EPS Applicability', { ...EPS_APPLICABILITY, validations: [] }],
    ['Gross Wages', GROSS_WAGES],
    ['Type Of Employee', EMPLOYEE_TYPE],
    ['Door No', DOOR_NO],
    ['Street', STREET],
    ['Area', AREA],
    ['City', CITY],
    ['State', STATE],
    ['Country', COUNTRY],
    ['PinCode', PINCODE],
    ['Higher Pension Applicability', HIGHER_PENSION_APPLICABILITY],
    ['Date Of Joining In EPF', DATE_OF_JOINING_IN_EPF],
    ['Spouse Name', SPOUSE_NAME],
    ['Scheme Certificate Number', SCHEME_CERTIFICATE_NUMBER],
    ['Pension Payment Order Number', PENSION_PAYMENT_ORDER_NUMBER],
    ['Date Of Exit From Previous Employment', DATE_OF_EXIT_FROM_PREVIOUS_EMPLOYMENT],
    ['ESI Number', ESI_NUMBER],
    ['Month-Working Days', MONTH_WORKING_DAYS],
    ['Worked Days', WORKED_DAYS],
    ['Basic Pay', BASIC_PAY],
    ['HRA', HRA],
    ['LTA', LTA],
    ['Perq Allowance', PERQ_ALLOWANCE],
    ['Special Allowance', SPECIAL_ALLOWANCE],
    ['Telephone Allowance', TELEPHONE_ALLOWANCE],
    ['Food Allowance', FOOD_ALLOWANCE],
    ['Gross Earnings', GROSS_EARNINGS],
    ['LOP', LOP],
    ['TDS', TDS],
    ['EPS', EPS],
    ['Advance Pay', ADVANCE_PAY],
    ['Salary Due (Net Salary)', SALARY_DUE_NET_SALARY],
    ['Nature of work', NATURE_OF_WORK],
    ['Daily hours of work', DAILY_HOURS_OF_WORK],
    ['Intervals of rest', INTERVALS_OF_REST],
    ['Overtime Wages', OVERTIME_WAGES]
  ])
